<template>
  <div
    class="rcs-test-recipient"
    :class="{ 'rcs-test-recipient--loading': loading }"
  >
    <div class="rcs-test-recipient__section">
      <p class="rcs-test-recipient__section-title">
        {{ t('templateBuilder.modals.sendTestV4.rcs.senderSection') }}
      </p>

      <label
        class="rcs-test-recipient__label"
        for="rcs-test-sender-select"
      >
        {{ t('templateBuilder.modals.sendTestV4.rcs.agentLabel') }}
      </label>
      <Dropdown
        id="rcs-test-sender-select"
        :model-value="selectedSenderId"
        :options="senderOptions"
        option-label="display_name"
        option-value="sender_id"
        class="rcs-test-recipient__dropdown w-full"
        :disabled="disabled || loading || !senderOptions.length"
        :placeholder="t('templateBuilder.modals.sendTestV4.rcs.agentPlaceholder')"
        @update:model-value="onSenderChange"
      >
        <template #value="slotProps">
          <span v-if="slotProps.value">
            {{ resolveSenderLabel(slotProps.value) }}
          </span>
          <span v-else class="rcs-test-recipient__placeholder">
            {{ t('templateBuilder.modals.sendTestV4.rcs.agentPlaceholder') }}
          </span>
        </template>
        <template #option="slotProps">
          <div class="rcs-test-recipient__option">
            <span>{{ slotProps.option.display_name }}</span>
            <span
              v-if="slotProps.option.source === 'shared'"
              class="rcs-test-recipient__badge"
            >
              {{ t('templateBuilder.modals.sendTestV4.rcs.sharedBadge') }}
            </span>
          </div>
        </template>
      </Dropdown>

      <div
        v-if="isSharedSender"
        class="rcs-test-recipient__shared-readonly"
      >
        <span class="rcs-test-recipient__readonly-label">
          {{ t('templateBuilder.modals.sendTestV4.rcs.sharedNumberLabel') }}
        </span>
        <code class="rcs-test-recipient__readonly-value">{{ selectedSenderId }}</code>
      </div>
    </div>

    <div
      v-if="!isSharedSender"
      class="rcs-test-recipient__section"
    >
      <p class="rcs-test-recipient__section-title">
        {{ t('templateBuilder.modals.sendTestV4.rcs.testNumbersSection') }}
      </p>
      <p class="rcs-test-recipient__hint">
        {{ t('templateBuilder.modals.sendTestV4.rcs.testNumbersHint') }}
      </p>

      <div
        v-if="!availableTestNumbers.length"
        class="rcs-test-recipient__empty"
        role="status"
      >
        {{ t('templateBuilder.modals.sendTestV4.rcs.noTestNumbers') }}
      </div>

      <div
        v-else
        class="rcs-test-recipient__chips"
        role="group"
        :aria-label="t('templateBuilder.modals.sendTestV4.rcs.testNumbersSection')"
      >
        <button
          v-for="number in availableTestNumbers"
          :key="number"
          type="button"
          class="rcs-test-recipient__chip"
          :class="{ 'rcs-test-recipient__chip--active': selectedTestNumbers.includes(number) }"
          :disabled="disabled || loading"
          @click="toggleNumber(number)"
        >
          <i
            class="fa-solid"
            :class="selectedTestNumbers.includes(number) ? 'fa-check' : 'fa-mobile-screen'"
            aria-hidden="true"
          />
          <span>{{ number }}</span>
        </button>
      </div>

      <div class="rcs-test-recipient__add-row">
        <div class="rcs-test-recipient__add-input">
          <div class="rcs-test-recipient__add-country">
            <CountryDropdown
              v-model="countryAndState"
              :configuration="countryDropdownConfig"
              :error="{ country: false, state: false }"
            />
          </div>
          <InputText
            v-model="newNumberLocal"
            class="rcs-test-recipient__add-phone"
            inputmode="numeric"
            maxlength="20"
            :placeholder="t('templateBuilder.modals.sendTestV4.rcs.addNumberPlaceholder')"
            :disabled="disabled || loading"
            @keyup.enter="addNumber"
          />
        </div>
        <Button
          type="button"
          class="p-button-outlined rcs-test-recipient__add-btn"
          icon="fa-solid fa-plus"
          :label="t('templateBuilder.modals.sendTestV4.rcs.addNumber')"
          :disabled="disabled || loading || !newNumberLocal.trim()"
          @click="addNumber"
        />
      </div>
      <small class="rcs-test-recipient__hint">
        {{ t('templateBuilder.modals.sendTestV4.rcs.addNumberHint') }}
      </small>

      <div
        v-if="canPersist && sessionExtraNumbers.length"
        class="rcs-test-recipient__persist"
      >
        <Button
          type="button"
          class="p-button-text p-button-sm"
          icon="fa-solid fa-floppy-disk"
          :label="t('templateBuilder.modals.sendTestV4.rcs.saveForAgent')"
          :loading="isSaving"
          :disabled="disabled || loading"
          @click="saveForAgent"
        />
      </div>

      <p
        v-if="!canPersist && selectedAgentDraft"
        class="rcs-test-recipient__warning"
        role="alert"
      >
        {{ t('templateBuilder.modals.sendTestV4.rcs.agentNotOperational') }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import { CountryCode } from 'libphonenumber-js';
import CountryDropdown from '@/components/fields/CountryDropdown.vue';
import { UserState } from '@/composables/User';
import {
  CountryAndStateConfig,
  CountryAndStateValues,
} from '@/types/country-state-types';
import { countriesPhonesCodes } from '@/configs/countries';
import {
  RcsAgent,
  RcsOnboardingStatus,
  RcsSelectableSender,
  normalizeRcsTestDevicePhoneNumber,
} from '@/types/rcs-onboarding';

export default defineComponent({
  name: 'RcsTestRecipientPanel',

  components: {
    Dropdown,
    Button,
    InputText,
    CountryDropdown,
  },

  props: {
    senderOptions: {
      type: Array as PropType<RcsSelectableSender[]>,
      default: () => [],
    },
    selectedSenderId: {
      type: String,
      default: '',
    },
    selectedAgent: {
      type: Object as PropType<RcsAgent | null>,
      default: null,
    },
    availableTestNumbers: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    selectedTestNumbers: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    sessionExtraNumbers: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    isSharedSender: {
      type: Boolean,
      default: true,
    },
    canPersist: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:selectedSenderId',
    'update:selectedTestNumbers',
    'add-test-number',
    'persist-test-numbers',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const newNumberLocal = ref('');
    const isSaving = ref(false);
    const countryAndState = ref<CountryAndStateValues>({
      country: (UserState.user.country ?? 'FR').toUpperCase() as CountryCode,
      state: '',
    });
    const countryDropdownConfig: CountryAndStateConfig = {
      showStates: false,
      showLabels: false,
      showFlags: true,
      showPhonesCodes: true,
      showOnlyPhoneCodeForSelection: true,
    };

    const selectedAgentDraft = computed(
      () => props.selectedAgent
        && props.selectedAgent.onboarding_status !== RcsOnboardingStatus.LAUNCHED,
    );

    const resolveSenderLabel = (senderId: string) => {
      const match = props.senderOptions.find((sender) => sender.sender_id === senderId);
      return match?.display_name || senderId;
    };

    const onSenderChange = (senderId: string) => {
      emit('update:selectedSenderId', senderId);
      emit('update:selectedTestNumbers', []);
    };

    const toggleNumber = (number: string) => {
      const next = props.selectedTestNumbers.includes(number)
        ? props.selectedTestNumbers.filter((n) => n !== number)
        : [...props.selectedTestNumbers, number];
      emit('update:selectedTestNumbers', next);
    };

    const buildE164FromLocal = (local: string): string => {
      const digits = local.replace(/\D/g, '');
      if (!digits) return '';
      const country = countryAndState.value.country?.toUpperCase() || '';
      const dialCode = countriesPhonesCodes[country] || '';
      const dialDigits = dialCode.replace(/\D/g, '');
      if (digits.startsWith(dialDigits)) {
        return normalizeRcsTestDevicePhoneNumber(`+${digits}`);
      }
      return normalizeRcsTestDevicePhoneNumber(`+${dialDigits}${digits}`);
    };

    const addNumber = () => {
      const normalized = buildE164FromLocal(newNumberLocal.value);
      if (!normalized) return;
      emit('add-test-number', normalized);
      newNumberLocal.value = '';
    };

    const saveForAgent = async () => {
      isSaving.value = true;
      try {
        emit('persist-test-numbers');
      } finally {
        isSaving.value = false;
      }
    };

    return {
      t,
      newNumberLocal,
      isSaving,
      countryAndState,
      countryDropdownConfig,
      selectedAgentDraft,
      resolveSenderLabel,
      onSenderChange,
      toggleNumber,
      addNumber,
      saveForAgent,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/components/rcs-onboarding/rcs-onboarding.scss';
</style>
